// householdsFixtures — the web-side corpus of §7.4 / §7.5, mirroring W0's
// server fixtures (apps/api/src/lib/sentItems/fixtures.ts) household by
// household.
//
// Same reason the Sent Items corpus exists: the two household pages are being
// built while W3 writes the routes they call, and a page nobody can look at is
// a page nobody reviews. Every shape here is the exported type from
// sentItemsClient.ts, so a field that drifts on the server fails to COMPILE
// here rather than rendering wrong.
//
// The six rows are the six §8.4 branches, and they are the six because each
// one is a shape the old Applications-plus-Households pair got wrong:
//
//   • RENEWAL_ONLY   — a household whose whole history with us is one renewal.
//                      No application language anywhere on its row (§1).
//   • RRA_ONLY       — an accommodation-only household, in BOTH viewer
//                      versions: on-roster, and the off-roster twin whose
//                      standing, in-motion count and last-activity are all
//                      computed over the visible set (T5). Pair them in a test.
//   • BLOCKED_SYNC   — a blocked write-back outranks everything in motion.
//   • PRE_CASE       — an applicant group with NO case: `householdId` is
//                      `group:<groupId>` and `caseId` is null (T15).
//   • THREE_IN_MOTION— several items moving, so the line carries "+N more".
//   • CLOSED         — nothing left to do; the newest completion is the line.
//
// The two detail fixtures are the two the pages actually have to handle
// differently: a household with in-motion AND finished items, and a pre-case
// group whose item list is empty and whose only action is the builder.

import type { Signal } from '@/lib/commandCenterClient';
import type {
  HouseholdDetailResponse,
  HouseholdIntakeSummary,
  HouseholdLeaseSummary,
  HouseholdListResponse,
  HouseholdListRow,
  HouseholdRelationship,
  HouseholdVerificationSummary,
  SentItemRow,
  WhereItStands,
} from '@/lib/sentItemsClient';
import { leaseDoneRow, leaseOutRow, rentalVerificationRow } from '@/lib/sentItemsFixtures';

const NO_SIGNALS = { block: 0, attention: 0, notice: 0 };
const ONE_BLOCK = { block: 1, attention: 0, notice: 0 };

function standing(line: string, itemId: string | null, tone: WhereItStands['tone']): WhereItStands {
  return { line, itemId, tone };
}

// The three operational summaries (sol finding 10), mirroring W0's server
// fixtures household by household. Named shapes rather than inline literals, so
// a row still reads as its STORY.

const NO_INTAKE: HouseholdIntakeSummary = {
  state: 'not_requested',
  requested: 0,
  completed: 0,
  stale: false,
  label: 'No intake requested',
};

const NO_VERIFICATIONS: HouseholdVerificationSummary = {
  total: 0,
  pending: 0,
  responded: 0,
  concern: false,
  cleared: false,
  label: null,
};

/** A household with no application group has no lease CTA at all. */
const NO_LEASE_CTA: HouseholdLeaseSummary = {
  state: 'none',
  label: '',
  href: null,
  note: null,
  secondaryLabel: null,
  secondaryHref: null,
};

function builderCta(groupId: string): HouseholdLeaseSummary {
  return {
    state: 'new',
    label: 'New lease',
    href: `/applications/${encodeURIComponent(groupId)}/lease`,
    note: null,
    secondaryLabel: null,
    secondaryHref: null,
  };
}

/** The post-send CTA: the item, not the builder. */
function itemCta(itemId: string, done: boolean): HouseholdLeaseSummary {
  return {
    state: done ? 'executed' : 'sent',
    label: done ? 'View lease' : 'Track signing',
    href: `/items/${encodeURIComponent(itemId)}`,
    note: null,
    secondaryLabel: null,
    secondaryHref: null,
  };
}

// The same households the server corpus names, so a row can be checked
// against W0's copy by eye.
const FAIRWEATHER = {
  subject: 'Dana Fairweather, Ruben Ortega-Lyle',
  property: 'Larkspur Court',
  unit: '412 NE Fern St #3',
  caseId: 'case-fairweather',
  groupId: 'grp-fairweather-01',
};

const NANDAKUMAR = {
  subject: 'Priya Nandakumar',
  property: 'Quillfeather Row',
  unit: '88 SW Marlow Ave #12',
  caseId: 'case-nandakumar',
  /** A renewal group is a `renewal-<ULID>` synthetic id (T16). */
  groupId: 'renewal-01ABCDEF01ABCDEF01ABCDEF01',
};

const ASH = {
  subject: 'Colton Ash',
  property: 'Sixmile Flats',
  unit: '19 N Tanager Ln #2',
  caseId: 'case-ash',
  rraCaseId: 'rra-case-ash-01',
};

const VOSS = {
  subject: 'Imelda Voss, Teodor Vossberg',
  property: 'Harrowgate Duplexes',
  unit: '605 E Kestrel St B',
  caseId: 'case-voss',
  groupId: 'grp-voss-04',
};

const BRICKLEBANK = {
  subject: 'Naomi Bricklebank',
  property: 'Larkspur Court',
  unit: '412 NE Fern St #7',
  groupId: 'grp-bricklebank-09',
};

// ---------------------------------------------------------------------------
// The six rows (§8.4's five branches, plus the pre-case shape)
// ---------------------------------------------------------------------------

/** §8.4 branch 3, the "only a renewal" household — no application language. */
export const HOUSEHOLD_RENEWAL_ONLY: HouseholdListRow = {
  householdId: NANDAKUMAR.caseId,
  caseId: NANDAKUMAR.caseId,
  groupId: NANDAKUMAR.groupId,
  name: NANDAKUMAR.subject,
  propertyLabel: NANDAKUMAR.property,
  unitLabel: NANDAKUMAR.unit,
  relationship: 'tenant',
  standing: standing(
    'Renewal out for signature · Priya Nandakumar · 3 days',
    `lease:${NANDAKUMAR.groupId}`,
    'active',
  ),
  inMotionCount: 1,
  signals: NO_SIGNALS,
  lastActivityAt: '2026-08-25T18:20:00.000Z',
  state: 'open',
  // A renewal skips intake and skips prior-landlord verification.
  intake: NO_INTAKE,
  verification: NO_VERIFICATIONS,
  lease: itemCta(`lease:${NANDAKUMAR.groupId}`, false),
  propertyId: 'prop-quillfeather',
  unitId: 'unit-marlow-12',
};

/** The "only an RRA" household as an ON-ROSTER viewer sees it (§11.8b). */
export const HOUSEHOLD_RRA_ONLY: HouseholdListRow = {
  householdId: ASH.caseId,
  caseId: ASH.caseId,
  groupId: null,
  name: ASH.subject,
  propertyLabel: ASH.property,
  unitLabel: ASH.unit,
  relationship: 'tenant',
  standing: standing(
    'Accommodation request with WHP for review · Beth Aldredge · 8 hours',
    `rra_intake:${ASH.rraCaseId}`,
    'active',
  ),
  inMotionCount: 2,
  signals: NO_SIGNALS,
  lastActivityAt: '2026-08-28T09:00:00.000Z',
  state: 'open',
  // ⚠️ IDENTICAL on the off-roster twin below (which is a spread of this row):
  // the three summaries are computed from lease / intake / verification only, so
  // an accommodation can never move them (T5, §5.7).
  intake: NO_INTAKE,
  verification: NO_VERIFICATIONS,
  lease: NO_LEASE_CTA,
  propertyId: 'prop-sixmile',
  unitId: 'unit-tanager-2',
};

/**
 * The SAME household to a viewer off the RRA roster.
 *
 * Nothing leaks — not through the standing line, not through the in-motion
 * count, not through `lastActivityAt` and therefore not through sort position
 * (T5, §5.7). Everything else is byte-identical to the on-roster row, which is
 * exactly what a test should assert.
 */
export const HOUSEHOLD_RRA_ONLY_OFF_ROSTER: HouseholdListRow = {
  ...HOUSEHOLD_RRA_ONLY,
  standing: standing('Nothing sent yet', null, 'empty'),
  inMotionCount: 0,
  lastActivityAt: null,
};

/** §8.4 branch 2 — a blocked write-back wins the standing line. */
export const HOUSEHOLD_BLOCKED_SYNC: HouseholdListRow = {
  householdId: VOSS.caseId,
  caseId: VOSS.caseId,
  groupId: VOSS.groupId,
  name: VOSS.subject,
  propertyLabel: VOSS.property,
  unitLabel: VOSS.unit,
  relationship: 'tenant',
  standing: standing(
    'Lease blocked · AppFolio rejected the upload: no matching occupancy for unit 605 E Kestrel St B',
    `lease:${VOSS.groupId}`,
    'blocked',
  ),
  inMotionCount: 1,
  signals: ONE_BLOCK,
  lastActivityAt: '2026-08-26T14:05:00.000Z',
  state: 'open',
  intake: NO_INTAKE,
  verification: NO_VERIFICATIONS,
  // The lease is SIGNED; what is stuck is the write-back behind it.
  lease: itemCta(`lease:${VOSS.groupId}`, true),
  propertyId: 'prop-harrowgate',
  unitId: 'unit-kestrel-b',
};

/** T15 — an applicant group with nothing sent. No case, still listed: this is
 *  where "New Lease" starts, so a list that hid it would hide the front door. */
export const HOUSEHOLD_PRE_CASE: HouseholdListRow = {
  householdId: `group:${BRICKLEBANK.groupId}`,
  caseId: null,
  groupId: BRICKLEBANK.groupId,
  name: BRICKLEBANK.subject,
  propertyLabel: BRICKLEBANK.property,
  unitLabel: BRICKLEBANK.unit,
  relationship: 'applying',
  standing: standing('Nothing sent yet · Applying', null, 'empty'),
  inMotionCount: 0,
  signals: NO_SIGNALS,
  lastActivityAt: null,
  state: 'open',
  intake: NO_INTAKE,
  verification: NO_VERIFICATIONS,
  // The reason this row has to exist at all (T15).
  lease: builderCta(BRICKLEBANK.groupId),
  propertyId: 'prop-larkspur',
  unitId: 'unit-fern-7',
};

/** §8.4 branch 3 with several in motion — the "+N more" tail. */
export const HOUSEHOLD_THREE_IN_MOTION: HouseholdListRow = {
  householdId: FAIRWEATHER.caseId,
  caseId: FAIRWEATHER.caseId,
  groupId: FAIRWEATHER.groupId,
  name: FAIRWEATHER.subject,
  propertyLabel: FAIRWEATHER.property,
  unitLabel: FAIRWEATHER.unit,
  relationship: 'approved',
  standing: standing(
    'Lease waiting on WHP · Miles Scruggs · 8 hours · +2 more in motion',
    `lease:${FAIRWEATHER.groupId}`,
    'active',
  ),
  inMotionCount: 3,
  signals: { block: 0, attention: 1, notice: 0 },
  lastActivityAt: '2026-08-28T09:10:00.000Z',
  state: 'open',
  // The row the old Applications page was FOR: an approved household that still
  // owes us intake answers and whose verification came back with something on
  // it. All three facts, on one line, without opening anything.
  intake: { state: 'partial', requested: 3, completed: 1, stale: true, label: 'Intake 1 of 3' },
  verification: {
    total: 2,
    pending: 1,
    responded: 1,
    concern: true,
    cleared: false,
    label: 'Verification concern',
  },
  lease: itemCta(`lease:${FAIRWEATHER.groupId}`, false),
  propertyId: 'prop-larkspur',
  unitId: 'unit-fern-3',
};

/** §8.4 branch 4 — everything finished; the newest completion is the line. */
export const HOUSEHOLD_CLOSED: HouseholdListRow = {
  householdId: 'case-quintanilla',
  caseId: 'case-quintanilla',
  groupId: 'grp-quintanilla-02',
  name: 'Marisol Quintanilla-Reyes',
  propertyLabel: 'Sixmile Flats',
  unitLabel: '19 N Tanager Ln #6',
  relationship: 'past',
  standing: standing('Lease fully executed · 13 Aug', 'lease:grp-quintanilla-02', 'done'),
  inMotionCount: 0,
  signals: NO_SIGNALS,
  lastActivityAt: '2026-08-14T20:05:00.000Z',
  state: 'closed',
  intake: { state: 'complete', requested: 3, completed: 3, stale: false, label: 'Intake complete' },
  verification: { total: 2, pending: 0, responded: 2, concern: false, cleared: false, label: null },
  lease: itemCta('lease:grp-quintanilla-02', true),
  propertyId: 'prop-sixmile',
  unitId: 'unit-tanager-6',
};

/** The corpus, in the order `filter=all` returns it (lastActivityAt desc,
 *  nulls last). The page renders what it is given and never re-sorts. */
export const HOUSEHOLD_LIST_FIXTURES: HouseholdListRow[] = [
  HOUSEHOLD_THREE_IN_MOTION,
  HOUSEHOLD_RRA_ONLY,
  HOUSEHOLD_BLOCKED_SYNC,
  HOUSEHOLD_RENEWAL_ONLY,
  HOUSEHOLD_CLOSED,
  HOUSEHOLD_PRE_CASE,
];

export function fixtureHouseholdListResponse(): HouseholdListResponse {
  return {
    items: HOUSEHOLD_LIST_FIXTURES,
    total: HOUSEHOLD_LIST_FIXTURES.length,
    nextCursor: null,
    withheld: [],
    generatedAt: '2026-08-28T17:00:00.000Z',
  };
}

// ---------------------------------------------------------------------------
// Detail — a household with items, and a pre-case group with none
// ---------------------------------------------------------------------------

/**
 * The items of a household file, in the order §8.3 fixes: in motion first (in
 * the `waiting` view's order), then finished, newest completion first.
 *
 * They are the Sent Items corpus rows, unchanged. A household's items ARE
 * sent items — one row per document for its whole life — and a second set of
 * row fixtures would be a second definition of the same thing.
 */
const HOUSEHOLD_ITEMS: SentItemRow[] = [leaseOutRow, rentalVerificationRow, leaseDoneRow];

/** A household with both kinds of item: two in motion, one finished. */
/**
 * Both detail fixtures are built as plain consts and THEN typed, rather than
 * as annotated object literals.
 *
 * That is deliberate and it is about the merge, not about style: W0's contract
 * fix (9e4c1d3) makes `HouseholdDetailResponse.activity` REQUIRED, and this
 * branch was cut before it. A fresh object literal with an annotation gets
 * TypeScript's excess-property check, so `activity` would fail to compile
 * HERE, today — while omitting it would fail to compile the moment the
 * contract merges. Assigning through a const satisfies both, because freshness
 * only applies to a literal written directly at the annotation.
 *
 * Delete the indirection once W0 is merged; nothing else changes.
 */
const FAIRWEATHER_FILE = {
  householdId: FAIRWEATHER.caseId,
  caseId: FAIRWEATHER.caseId,
  groupId: FAIRWEATHER.groupId,
  header: {
    name: FAIRWEATHER.subject,
    people: [
      {
        name: 'Dana Fairweather',
        isLead: true,
        type: 'applicant',
        email: 'dana@example.com',
        isMinor: false,
        comingOfAge: null,
      },
      {
        name: 'Ruben Ortega-Lyle',
        isLead: false,
        type: 'applicant',
        email: 'ruben@example.com',
        isMinor: false,
        comingOfAge: null,
      },
      {
        name: 'Tilly Ortega-Lyle',
        isLead: false,
        type: 'occupant',
        email: null,
        isMinor: true,
        comingOfAge: '2029-04-02',
      },
    ],
    propertyLabel: FAIRWEATHER.property,
    unitLabel: FAIRWEATHER.unit,
    // Pinned because the enclosing literal is no longer annotated (see the
    // note above): a bare string literal in a mutable property widens to
    // `string`, and an assertion is what holds it to the union.
    relationship: 'approved' as HouseholdRelationship,
    afStatusLabel: 'Approved',
    freshAsOf: '2026-08-28T16:40:00.000Z',
    leaseTerm: { start: '1 Sep 2026', end: '31 Aug 2027', rent: '$1,850 / month' },
    actions: [
      {
        id: 'builder',
        label: 'Lease builder',
        enabled: true,
        blockedReason: null,
        href: `/applications/${FAIRWEATHER.groupId}/lease`,
      },
      {
        id: 'payment-plan',
        label: 'Payment plan',
        enabled: true,
        blockedReason: null,
        href: '/send/payment-plan',
      },
      {
        id: 'renewal',
        label: 'Renewal',
        enabled: false,
        blockedReason: 'The current lease has not been executed yet.',
        href: null,
      },
    ],
    standing: standing(
      'Lease out for signature · Marcus Fairweather · 2 days · +1 more in motion',
      leaseOutRow.itemId,
      'active',
    ),
  },
  items: HOUSEHOLD_ITEMS,
  signals: [
    {
      id: 'sig-fairweather-1',
      severity: 'attention',
      category: 'signing_stalled',
      categoryLabel: 'Signing stalled',
      message: 'The lease has been with Marcus Fairweather for 2 days with no activity.',
      objectType: 'thread',
      caseId: FAIRWEATHER.caseId,
      threadId: `lease:${FAIRWEATHER.groupId}`,
      source: 'lease_signatures',
      since: '2026-08-26T17:00:00.000Z',
      ageDays: 2,
      href: `/items/${encodeURIComponent(leaseOutRow.itemId)}`,
      resolutionId: 'res-fairweather-1',
      resolution: null,
    },
  ] satisfies Signal[],
  withheld: [
    {
      kind: 'rra_intake',
      label: 'Accommodation requests',
      reason: 'They are restricted to the accommodation roster.',
    },
  ],
  // Newest FIRST, and capped at 50 by the API — the OPPOSITE of an item's own
  // activity (T18), on purpose. The page renders it in the order it arrives.
  activity: [
    { at: '2026-08-28T09:10:00.000Z', text: 'Reminded Marcus Fairweather about the lease' },
    { at: '2026-08-26T17:00:00.000Z', text: 'Lease sent for signature to 4 people' },
    { at: '2026-08-26T16:40:00.000Z', text: 'Lease generated — version 2' },
    { at: '2026-08-14T15:05:00.000Z', text: 'Intake completed by Dana Fairweather' },
  ],
  durableCase: {
    caseId: FAIRWEATHER.caseId,
    householdName: 'Fairweather · Ortega-Lyle',
    unitId: 'unit-412-fern-3',
    currentGroupId: FAIRWEATHER.groupId,
    startedAt: '2025-08-02T15:00:00.000Z',
    lastLeaseAt: '2026-08-26T17:00:00.000Z',
    members: [
      {
        groupId: FAIRWEATHER.groupId,
        linkedVia: 'application',
        linkedAt: '2025-08-02T15:00:00.000Z',
        isCurrent: true,
        isThisGroup: true,
      },
      {
        groupId: 'renewal-01FAIRWEATHER0001',
        linkedVia: 'renewal',
        linkedAt: '2026-06-14T18:30:00.000Z',
        isCurrent: false,
        isThisGroup: false,
      },
    ],
    threads: [],
  },
  generatedAt: '2026-08-28T17:00:00.000Z',
};

export const householdWithItemsDetail: HouseholdDetailResponse = FAIRWEATHER_FILE;

/**
 * The pre-case group (T15).
 *
 * Its `householdId` is `group:<groupId>`, it has no case, no signals, no
 * durable case and NO ITEMS — and the page must not go looking for a docket
 * for it. Its one action is the builder, because starting the lease is
 * genuinely the only thing there is to do here.
 */
const BRICKLEBANK_FILE = {
  householdId: `group:${BRICKLEBANK.groupId}`,
  caseId: null,
  groupId: BRICKLEBANK.groupId,
  header: {
    name: BRICKLEBANK.subject,
    people: [
      {
        name: 'Naomi Bricklebank',
        isLead: true,
        type: 'applicant',
        email: 'naomi@example.com',
        isMinor: false,
        comingOfAge: null,
      },
    ],
    propertyLabel: BRICKLEBANK.property,
    unitLabel: BRICKLEBANK.unit,
    // Pinned because the enclosing literal is no longer annotated (see the
    // note above): a bare string literal in a mutable property widens to
    // `string`, and an assertion is what holds it to the union.
    relationship: 'applying' as HouseholdRelationship,
    afStatusLabel: 'In screening',
    freshAsOf: '2026-08-28T16:40:00.000Z',
    actions: [
      {
        id: 'builder',
        label: 'New lease',
        enabled: true,
        blockedReason: null,
        href: `/applications/${BRICKLEBANK.groupId}/lease`,
      },
    ],
    standing: standing('Nothing sent yet · Applying', null, 'empty'),
  },
  items: [],
  signals: [],
  withheld: [],
  // Nothing has happened to this household yet, said as an empty array rather
  // than a missing field — the page renders no Activity heading over it.
  activity: [],
  generatedAt: '2026-08-28T17:00:00.000Z',
};

export const preCaseHouseholdDetail: HouseholdDetailResponse = BRICKLEBANK_FILE;

export const HOUSEHOLD_DETAIL_FIXTURES: Record<string, HouseholdDetailResponse> = {
  [householdWithItemsDetail.householdId]: householdWithItemsDetail,
  [preCaseHouseholdDetail.householdId]: preCaseHouseholdDetail,
};

/** The detail the dev-only fixtures mode serves for a household, when it has
 *  one. Nothing is invented for an id with no fixture. */
export function fixtureHouseholdDetail(householdId: string): HouseholdDetailResponse | null {
  return HOUSEHOLD_DETAIL_FIXTURES[householdId] ?? null;
}
